import formatGroupedValues from "../utils/formatGroupedValues";
import capitalizeFilterLabel from "../utils/capitalizeFilterLabel";
import { dedupeClientFilterOptions } from "../utils/dedupeClientFilterOptions";
import FilterField from "../fields/FilterField";
import FilterMultiSelect from "../fields/FilterMultiSelect";
import FilterSelect from "../fields/FilterSelect";
import FilterToggle from "../fields/FilterToggle";

export const CLIENT_LIST_SORT_OPTIONS = {
  CLIENTS: [
    { value: "all", label: "All" },
    { value: "name", label: "Name" },
    { value: "parent", label: "Parent Client" },
    { value: "status", label: "Status" },
  ],
  CONTACTS: [
    { value: "first_name", label: "First Name" },
    { value: "last_name", label: "Last Name" },
  ],
  POSITIONS: [{ value: "name", label: "Name" }],
};

export const CLIENT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "donotservice", label: "Do not service" },
  { value: "prospect", label: "Prospect" },
  { value: "closed", label: "Closed" },
];

export const CONTACT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "pending_activation", label: "Pending Activation" },
];

export const CONTACT_ROLE_OPTIONS = [
  { value: "Billing", label: "Billing" },
  { value: "Other", label: "Other" },
  { value: "Sign-off Manager", label: "Sign-off Manager" },
  { value: "Job Supervisor", label: "Job Supervisor" },
];

const SORT_LABEL_MAP = {
  all: "All",
  name: "Name",
  parent: "Parent Client",
  status: "Status",
  first_name: "First Name",
  last_name: "Last Name",
};

function FieldWrapper({ fieldId, label, focusedFieldId, onFocusClear, children }) {
  return (
    <FilterField
      id={fieldId}
      label={label}
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
    >
      {children}
    </FilterField>
  );
}

export function ClientFilterFields({
  selectedTab,
  query,
  patchQuery,
  focusedFieldId,
  onFocusClear,
  divisionOptions = [],
  contactClientOptions = [],
}) {
  const patch = patchQuery;
  const divisionSelectOptions = divisionOptions.map((d) => ({
    value: String(d.value ?? d.id ?? d.division_id ?? d.name),
    label: d.label ?? d.name ?? String(d.value ?? d.id),
  }));
  const clientSelectOptions = dedupeClientFilterOptions(
    contactClientOptions.map((c) => ({
      value: String(c.value ?? c.id ?? c.client_id),
      label: c.label ?? c.name ?? String(c.value ?? c.id),
    })),
  );
  const sortOptions = CLIENT_LIST_SORT_OPTIONS[selectedTab] || CLIENT_LIST_SORT_OPTIONS.CLIENTS;

  return (
    <>
      {selectedTab === "CONTACTS" ? (
        <>
          <FieldWrapper fieldId="contactRole" label="Role" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="contact-role" options={CONTACT_ROLE_OPTIONS} value={query.contactRoleFilter || []} onChange={(v) => patch({ contactRoleFilter: v })} />
          </FieldWrapper>
          <FieldWrapper fieldId="contactClient" label="Client" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="contact-client" options={clientSelectOptions} value={query.contactClientFilter || []} onChange={(v) => patch({ contactClientFilter: v })} />
          </FieldWrapper>
          <FieldWrapper fieldId="status" label="Status" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="contact-status" options={CONTACT_STATUS_OPTIONS} value={query.statusFilter || []} onChange={(v) => patch({ statusFilter: v })} formatLabel={capitalizeFilterLabel} />
          </FieldWrapper>
        </>
      ) : selectedTab === "POSITIONS" ? (
        <>
          <FieldWrapper fieldId="divisions" label="Divisions" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="position-divisions" options={divisionSelectOptions} value={(query.divisionFilter || []).map(String)} onChange={(v) => patch({ divisionFilter: v.map((x) => Number(x) || x) })} />
          </FieldWrapper>
          <FieldWrapper fieldId="positionClient" label="Client" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="position-client" options={clientSelectOptions} value={query.positionClientFilter || []} onChange={(v) => patch({ positionClientFilter: v })} />
          </FieldWrapper>
        </>
      ) : (
        <>
          <FieldWrapper fieldId="divisions" label="Divisions" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="client-divisions" options={divisionSelectOptions} value={(query.divisionFilter || []).map(String)} onChange={(v) => patch({ divisionFilter: v.map((x) => Number(x) || x) })} />
          </FieldWrapper>
          <FieldWrapper fieldId="status" label="Status" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
            <FilterMultiSelect id="client-status" options={CLIENT_STATUS_OPTIONS} value={query.statusFilter || []} onChange={(v) => patch({ statusFilter: v })} formatLabel={capitalizeFilterLabel} />
          </FieldWrapper>
        </>
      )}
      <FieldWrapper fieldId="showDeleted" label="" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterToggle id="client-show-deleted" label="Show Deleted" checked={!!query.showDeleted} onChange={(v) => patch({ showDeleted: v })} />
      </FieldWrapper>
      <FilterSelect id="client-sort-by" label="Sort By" value={query.filterSortBy} onChange={(v) => patch({ filterSortBy: v })} options={sortOptions} placeholder={null} />
      <FilterSelect id="client-sort-direction" label="Sort Direction" value={query.filterSortDirection} onChange={(v) => patch({ filterSortDirection: v })} options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]} placeholder={null} />
    </>
  );
}

export function buildClientListFilterChips(selectedTab, query, { divisionOptions = [], contactClientOptions = [] } = {}) {
  const chips = [];
  const labelForDivision = (id) => {
    const match = divisionOptions.find(
      (o) => String(o.value ?? o.id ?? o.division_id) === String(id),
    );
    return match?.label ?? match?.name ?? String(id);
  };
  const labelForClient = (id) => contactClientOptions.find((o) => String(o.value ?? o.id ?? o.client_id) === String(id))?.label ?? contactClientOptions.find((o) => String(o.value ?? o.id) === String(id))?.name ?? String(id);

  if (selectedTab !== "CONTACTS" && (query.divisionFilter || []).length > 0) {
    chips.push({ id: "divisions", key: "divisionFilter", fieldId: "divisions", label: `Divisions: ${formatGroupedValues(query.divisionFilter, labelForDivision, 3)}` });
  }
  if (selectedTab !== "POSITIONS" && (query.statusFilter || []).length > 0) {
    chips.push({ id: "status", key: "statusFilter", fieldId: "status", label: `Status: ${formatGroupedValues(query.statusFilter, capitalizeFilterLabel, 3)}` });
  }
  if (selectedTab === "CONTACTS") {
    if ((query.contactRoleFilter || []).length > 0) chips.push({ id: "role", key: "contactRoleFilter", fieldId: "contactRole", label: `Role: ${formatGroupedValues(query.contactRoleFilter, (v) => v, 3)}` });
    if ((query.contactClientFilter || []).length > 0) chips.push({ id: "client", key: "contactClientFilter", fieldId: "contactClient", label: `Client: ${formatGroupedValues(query.contactClientFilter, labelForClient, 3)}` });
  }
  if (selectedTab === "POSITIONS" && (query.positionClientFilter || []).length > 0) {
    chips.push({ id: "client", key: "positionClientFilter", fieldId: "positionClient", label: `Client: ${formatGroupedValues(query.positionClientFilter, labelForClient, 3)}` });
  }
  if (query.showDeleted) chips.push({ id: "deleted", key: "showDeleted", fieldId: "showDeleted", label: "Show Deleted" });
  const sortKey = query.filterSortBy;
  const showSortChip = selectedTab === "CLIENTS" ? sortKey && sortKey !== "all" : selectedTab === "CONTACTS" ? sortKey === "last_name" : false;
  if (showSortChip) chips.push({ id: "sort", key: "filterSortBy", fieldId: "client-sort-by", label: `Sort: ${SORT_LABEL_MAP[sortKey] || sortKey}` });
  return chips;
}

export function countClientListActiveFilters(selectedTab, query) {
  return buildClientListFilterChips(selectedTab, query).length;
}
